import { useState } from 'react';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { programs } from '../mock';
import { useI18n } from '../contexts/I18nContext';

// Map program keys → translation key prefixes. Images, hrefs, and the
// "show CTA" flag still come from mock.js so admins can edit them later
// via the CMS; only the text changes per language.
const PROGRAM_T_PREFIX = {
  arts: 'prog.arts',
  enrichment: 'prog.enrich',
  community: 'prog.comm',
  space: 'prog.space',
};

export default function Programs() {
  const { t } = useI18n();
  const [active, setActive] = useState(programs[0].key);
  const current = programs.find((p) => p.key === active);
  const prefix = PROGRAM_T_PREFIX[current?.key] || 'prog.arts';

  return (
    <section id="programs" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
            <span className="h-[2px] w-8 bg-[#E5614C]" /> {t('home.programs.eyebrow')}
          </div>
          <h2 className="font-serif-display text-[40px] sm:text-[48px] lg:text-[56px] leading-[1.05] font-bold text-[#2A1B1A] mt-4">
            {t('home.programs.title1')} <span className="italic text-[#E5614C]">{t('home.programs.title2')}</span>
          </h2>
          <p className="mt-4 text-[16px] text-[#2A1B1A]/70">
            {t('home.programs.subtitle')}
          </p>
        </div>

        {/* Narrative thread — reinforces that programs are entry points, not silos */}
        <div className="mt-10 max-w-3xl mx-auto rounded-2xl bg-[#FAF3EE] border border-[#2A1B1A]/8 p-6 sm:p-7 text-center" data-testid="programs-thread">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#E5614C]">{t('home.programs.threadEyebrow')}</div>
          <p className="mt-2.5 font-serif-display text-[19px] sm:text-[22px] font-bold text-[#2A1B1A] leading-snug">
            <span className="text-[#E5614C]">{t('home.programs.thread1')}</span>{' '}
            <span className="text-[#FCD968]">{t('home.programs.thread2')}</span>{' '}
            <span className="text-[#5DCAB5]">{t('home.programs.thread3')}</span>{' '}
            <span>{t('home.programs.thread4')}</span>
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap justify-center gap-2 lg:gap-3">
          {programs.map((p) => {
            const pPrefix = PROGRAM_T_PREFIX[p.key] || 'prog.arts';
            return (
              <button
                key={p.key}
                onClick={() => setActive(p.key)}
                className={`px-5 lg:px-7 h-11 rounded-full text-[14px] font-semibold transition-[background-color,color,box-shadow] duration-300 ${
                  active === p.key
                    ? 'bg-[#E5614C] text-white shadow-[0_8px_20px_-6px_rgba(229,97,76,0.6)]'
                    : 'bg-[#FAF3EE] text-[#2A1B1A] hover:bg-[#E5614C]/10 hover:text-[#E5614C]'
                }`}
                data-testid={`prog-tab-${p.key}`}
              >
                {t(`${pPrefix}.tab`)}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="mt-12 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="relative rounded-[28px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(42,27,26,0.35)] order-2 lg:order-1">
            <img
              key={current.image}
              src={current.image}
              alt={t(`${prefix}.title`)}
              className="w-full h-[480px] lg:h-[560px] object-cover animate-fade-up"
            />
            <div className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[#E5614C] text-xs font-semibold">
              {t(`${prefix}.tab`)}
            </div>
          </div>

          <div key={current.key} className="animate-fade-up order-1 lg:order-2">
            <h3 className="font-serif-display text-[34px] lg:text-[44px] leading-tight font-bold text-[#2A1B1A]">
              {t(`${prefix}.title`)}
            </h3>
            <p className="mt-5 text-[16px] text-[#2A1B1A]/70 leading-relaxed">
              {t(`${prefix}.desc`)}
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {['b1', 'b2', 'b3', 'b4'].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[#2A1B1A]">
                  <CheckCircle2 className="h-5 w-5 text-[#5DCAB5] mt-0.5 shrink-0" />
                  <span className="text-[15px]">{t(`${prefix}.${b}`)}</span>
                </li>
              ))}
            </ul>
            {current.cta && (
              <Button asChild className="mt-9 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold h-12 px-7 btn-press group">
                <Link to={current.ctaHref || '/get-involved'}>
                  {t(`${prefix}.cta`)} <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
